import { useState, useEffect, useCallback, useMemo } from 'react';
import { getLogisticsObjectIdKey, LogisticsObjectData, type LogisticsObjectEntity } from '@/types/logisticsObject';
import { getLogisticsObjectsApi } from '@/services/bolApiClient';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { extractErrorMessage } from '@/utils/errorUtils';
import { ACL, ChangeRequest, LogisticsObjectMetadata } from '@/api/bol';

interface UseLogisticsObjectResult {
  // Data
  objectData: LogisticsObjectData | null;
  metadata: LogisticsObjectMetadata | null;
  changeRequests: ChangeRequest[];
  acl: ACL | null;
  
  // Loading states
  objectLoading: boolean;
  metadataLoading: boolean;
  changeRequestsLoading: boolean;
  aclLoading: boolean;
  
  // Pagination for change requests
  changeRequestsPagination: {
    total: number;
    page: number;
    pageSize: number;
  };
  
  // Methods
  refetchObject: (revision?: number, at?: string) => Promise<void>;
  refetchMetadata: (revision?: number, at?: string) => Promise<void>;
  refetchChangeRequests: (page?: number, pageSize?: number) => Promise<void>;
  refetchACL: (revision?: number, at?: string) => Promise<void>;
  getChangeRequestById: (changeRequestId: string) => Promise<ChangeRequest | null>;
  compareRevisions: (rev1: number, rev2: number) => Promise<{ original: LogisticsObjectData; modified: LogisticsObjectData } | null>;
}

export function useLogisticsObject(
  logisticsObjectType: LogisticsObjectEntity,
  objectId: string
): UseLogisticsObjectResult {
  const { showError } = useSnackbarNotification();
  const api = useMemo(
    () => getLogisticsObjectsApi(logisticsObjectType),
    [logisticsObjectType],
  );
  const idKey = useMemo(
    () => getLogisticsObjectIdKey(logisticsObjectType),
    [logisticsObjectType],
  );
  const buildIdParams = useCallback(
    () => ({ [idKey]: objectId } as Record<string, string>),
    [idKey, objectId],
  );

  // State
  const [objectData, setObjectData] = useState<LogisticsObjectData | null>(null);
  const [metadata, setMetadata] = useState<LogisticsObjectMetadata | null>(null);
  const [changeRequests, setChangeRequests] = useState<ChangeRequest[]>([]);
  const [acl, setACL] = useState<ACL | null>(null);

  // Loading states
  const [objectLoading, setObjectLoading] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [changeRequestsLoading, setChangeRequestsLoading] = useState(false);
  const [aclLoading, setACLLoading] = useState(false);

  // Pagination
  const [changeRequestsPagination, setChangeRequestsPagination] = useState({
    total: 0,
    page: 0,
    pageSize: 10
  });

  // Fetch object data
  const refetchObject = useCallback(async (revision?: number, at?: string) => {
    if (!objectId) return;

    setObjectLoading(true);
    try {
      const data = await api.getLogisticsObjectById({
        ...buildIdParams(),
        revision: revision?.toString(),
        at,
      });
      setObjectData(data);
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error, `Failed to fetch ${logisticsObjectType} data`);
      showError(errorMessage);
    } finally {
      setObjectLoading(false);
    }
  }, [api, buildIdParams, logisticsObjectType, objectId, showError]);

  // Fetch metadata
  const refetchMetadata = useCallback(async (_revision?: number, _at?: string) => {
    if (!objectId) return;

    setMetadataLoading(true);
    try {
      const metadataData = await api.getLogisticsObjectMetadataById(
        buildIdParams(),
      );
      setMetadata(metadataData);
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error, `Failed to fetch ${logisticsObjectType} metadata`);
      showError(errorMessage);
    } finally {
      setMetadataLoading(false);
    }
  }, [api, buildIdParams, logisticsObjectType, objectId, showError]);

  // Fetch change requests
  const refetchChangeRequests = useCallback(async (page: number = 0, pageSize: number = 10) => {
    if (!objectId) return;

    setChangeRequestsLoading(true);
    try {
      const response = await api.listChangeRequestsOfLogisticsObjectById({
        ...buildIdParams(),
        limit: pageSize,
        offset: page * pageSize,
      });
      setChangeRequests(response.results);
      setChangeRequestsPagination({
        total: response.pagination.total,
        page,
        pageSize
      });
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error, `Failed to fetch ${logisticsObjectType} change requests`);
      showError(errorMessage);
    } finally {
      setChangeRequestsLoading(false);
    }
  }, [api, buildIdParams, logisticsObjectType, objectId, showError]);

  // Fetch ACL
  const refetchACL = useCallback(async (revision?: number, at?: string) => {
    if (!objectId) return;

    setACLLoading(true);
    try {
      const aclData = await api.getLogisticsObjectACLById({
        ...buildIdParams(),
        revision: revision?.toString(),
        at,
      });
      setACL(aclData);
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error, `Failed to fetch ${logisticsObjectType} ACL`);
      showError(errorMessage);
    } finally {
      setACLLoading(false);
    }
  }, [api, buildIdParams, logisticsObjectType, objectId, showError]);

  // Get specific change request
  const getChangeRequestById = useCallback(async (changeRequestId: string): Promise<ChangeRequest | null> => {
    if (!objectId) return null;

    try {
      const changeRequest = await api.getChangeRequestOnLogisticsObjectById({
        ...buildIdParams(),
        changeRequestId,
      });
      return changeRequest;
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error, 'Failed to fetch change request');
      showError(errorMessage);
      return null;
    }
  }, [api, buildIdParams, objectId, showError]);

  // Compare two revisions
  const compareRevisions = useCallback(async (rev1: number, rev2: number): Promise<{ original: LogisticsObjectData; modified: LogisticsObjectData } | null> => {
    if (!objectId) return null;

    try {
      const [original, modified] = await Promise.all([
        api.getLogisticsObjectById({
          ...buildIdParams(),
          revision: rev1.toString(),
        }),
        api.getLogisticsObjectById({
          ...buildIdParams(),
          revision: rev2.toString(),
        }),
      ]);

      return { original, modified };
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error, 'Failed to compare revisions');
      showError(errorMessage);
      return null;
    }
  }, [api, buildIdParams, objectId, showError]);

  // Initial data fetch
  useEffect(() => {
    if (objectId) {
      refetchObject();
      refetchMetadata();
      refetchChangeRequests();
      refetchACL();
    }
  }, [objectId, refetchObject, refetchMetadata, refetchChangeRequests, refetchACL]);

  return {
    objectData,
    metadata,
    changeRequests,
    acl,
    objectLoading,
    metadataLoading,
    changeRequestsLoading,
    aclLoading,
    changeRequestsPagination,
    refetchObject,
    refetchMetadata,
    refetchChangeRequests,
    refetchACL,
    getChangeRequestById,
    compareRevisions
  };
}